import { useState } from "react";
import { api, type APIResult } from "../../api/client";
import { useAppState } from "../../hooks/useAppState";
import { PageHeader } from "../PageHeader";
import { ResponseCard } from "../ResponseCard";
import { SectionCard } from "../SectionCard";

// Ações de controle de ciclo de vida: Cancel + Retry.
//
// Cancel: muda o status para "cancelled" (apenas em estados não-terminais).
// Retry: cria um *novo* job reusando o payload original — devolve um
//   novo job_id, que é automaticamente "tracked" pelo app.
export function TabJobActions() {
  const { trackedJobId, pushJobId, setTrackedJobId } = useAppState();
  const [input, setInput] = useState(trackedJobId ?? "");
  const [result, setResult] = useState<APIResult<unknown> | null>(null);
  const [running, setRunning] = useState<"none" | "cancel" | "retry">("none");

  async function cancel() {
    if (!input) return;
    setRunning("cancel");
    setResult(null);
    const r = await api.cancelJob(input.trim());
    setResult(r);
    setRunning("none");
  }

  async function retry() {
    if (!input) return;
    setRunning("retry");
    setResult(null);
    const r = await api.retryJob(input.trim());
    setResult(r);
    setRunning("none");
    const data = r.data as { new_job_id?: string } | null;
    if (r.ok && data?.new_job_id) {
      pushJobId(data.new_job_id);
      setTrackedJobId(data.new_job_id);
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Cancelar / Retry"
        subtitle="Operações de controle do ciclo de vida do job."
      />

      <SectionCard title="Job alvo">
        <input
          type="text"
          className="ks-input ks-code"
          placeholder="job_id (UUID)"
          value={input}
          onChange={(e) => setInput(e.target.value.trim())}
        />
      </SectionCard>

      <div className="grid gap-4 md:grid-cols-2">
        <SectionCard
          title="Cancelar"
          description="POST /jobs/:id/cancel — Marca como `cancelled`. Só funciona em estados não-terminais (queued/processing)."
          actions={
            <button
              type="button"
              className="ks-btn-primary text-xs"
              disabled={!input || running !== "none"}
              onClick={cancel}
            >
              {running === "cancel" ? "cancelando..." : "Cancelar agora"}
            </button>
          }
        >
          <p className="text-sm text-ink-600">
            Em jobs já terminais, o endpoint devolve <code className="ks-code">409 CONFLICT</code>{" "}
            com código <code className="ks-code">INVALID_STATE_TRANSITION</code>.
          </p>
        </SectionCard>

        <SectionCard
          title="Retry"
          description="POST /jobs/:id/retry — Cria um novo job a partir do payload original. Devolve `new_job_id` em 202."
          actions={
            <button
              type="button"
              className="ks-btn-primary text-xs"
              disabled={!input || running !== "none"}
              onClick={retry}
            >
              {running === "retry" ? "duplicando..." : "Retry agora"}
            </button>
          }
        >
          <p className="text-sm text-ink-600">
            O job original mantém o estado original (ex.: failed). O novo job começa em{" "}
            <code className="ks-code">queued</code>.
          </p>
        </SectionCard>
      </div>

      <ResponseCard result={result} loading={running !== "none"} />
    </div>
  );
}
